// TransportTab.jsx - Transport Management Tab for SuperAdminDashboard
import React, { useState, useEffect } from 'react';
import { 
  Car, Plus, Edit2, Trash2, Star, CheckCircle, XCircle, Clock, Eye,
  MapPin, Users, DollarSign, Shield, Plane
} from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const vehicleTypeOptions = [
  'Automóvil Particular', 'Automóvil Ejecutivo', 'Automóvil de Lujo', 'Vehículo Blindado',
  'Camioneta', 'SUV', 'Van', 'Sprinter', 'Minibús', 'Autobús'
];

const serviceLevelOptions = ['Básico', 'Ejecutivo', 'Premium', 'Lujo', 'VIP', 'Blindado'];

const amenityOptions = [
  'Aire acondicionado', 'WiFi', 'Cargadores', 'Bebidas', 'Interior de lujo',
  'Sonido premium', 'Cristales polarizados', 'Seguridad adicional', 'Espacio para equipo',
  'Asistencia bilingüe', 'Chofer bilingüe', 'Facturación disponible'
];

const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-white/10', text: 'text-white/60', label: 'Borrador' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pendiente' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Aprobado' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rechazado' },
    changes_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Cambios' },
  };
  const { bg, text, label } = config[status] || config.draft;
  return <span className={`px-2 py-1 rounded-full text-xs ${bg} ${text}`}>{label}</span>;
};

export default function TransportTab({ token, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all');

  const [form, setForm] = useState({
    business_name: { es: '', en: '' },
    provider_name: '',
    slug: '',
    description: { es: '', en: '' },
    city: '',
    country: 'México',
    coverage_areas: [],
    phone: '',
    whatsapp: '',
    email: '',
    cover_image: '',
    gallery: [],
    vehicle_types: [],
    brand: '',
    model_name: '',
    year: '',
    passenger_capacity: 0,
    luggage_capacity: '',
    units_available: 1,
    with_driver: true,
    without_driver: false,
    by_hour: true,
    by_day: true,
    by_transfer: true,
    by_event: true,
    night_availability: false,
    out_of_town: false,
    airport_service: false,
    tours_long_trips: false,
    service_levels: [],
    amenities: [],
    pricing: {
      price_per_hour: 0,
      price_per_day: 0,
      price_per_transfer: 0,
      price_per_event: 0,
      quote_only: false
    },
    requirements: {
      id_required: false,
      contract_required: false,
      advance_payment: false,
      cancellation_policy: ''
    },
    status: 'draft',
    featured: false
  });

  useEffect(() => {
    fetchItems();
  }, [token]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/transport?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching transport:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleSave = async () => {
    try {
      const data = {
        ...form,
        slug: form.slug || generateSlug(form.business_name.es || form.business_name.en || 'servicio')
      };

      if (editing) {
        await axios.put(`${API}/admin/transport/${editing}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Transporte actualizado');
      } else {
        await axios.post(`${API}/admin/transport`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Transporte creado');
      }

      setEditing(null);
      setCreating(false);
      fetchItems();
      onRefresh?.();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este servicio de transporte?')) return;
    try {
      await axios.delete(`${API}/admin/transport/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminado');
      fetchItems();
      onRefresh?.();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await axios.post(`${API}/admin/transport/${id}/toggle-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchItems();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleApprove = async (id, status) => {
    try {
      await axios.post(`${API}/admin/review/transport/${id}`, 
        { status, notes: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status === 'approved' ? 'Aprobado' : 'Rechazado');
      fetchItems();
      onRefresh?.();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const startEdit = (item) => {
    setForm({ ...form, ...item });
    setEditing(item.transport_id);
    setCreating(false);
  };

  const startCreate = () => {
    setForm({
      business_name: { es: '', en: '' },
      provider_name: '',
      slug: '',
      description: { es: '', en: '' },
      city: '',
      country: 'México',
      coverage_areas: [],
      phone: '',
      whatsapp: '',
      email: '',
      cover_image: '',
      gallery: [],
      vehicle_types: [],
      brand: '',
      model_name: '',
      year: '',
      passenger_capacity: 0,
      luggage_capacity: '',
      units_available: 1,
      with_driver: true,
      service_levels: [],
      amenities: [],
      pricing: { quote_only: true },
      requirements: {},
      status: 'approved',
      featured: false
    });
    setCreating(true);
    setEditing(null);
  };

  const toggleArrayItem = (field, value) => {
    const current = form[field] || [];
    if (current.includes(value)) {
      setForm({ ...form, [field]: current.filter(v => v !== value) });
    } else {
      setForm({ ...form, [field]: [...current, value] });
    }
  };

  const filteredItems = items.filter(item => {
    if (filter === 'all') return true;
    return item.status === filter;
  });

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  }

  // Editor View
  if (editing || creating) {
    return (
      <div className="space-y-6" data-testid="transport-editor">
        <div className="flex items-center justify-between">
          <h2 className="text-xl text-white font-semibold">
            {creating ? 'Nuevo Servicio de Transporte' : 'Editar Transporte'}
          </h2>
          <div className="flex gap-2">
            <button onClick={() => { setEditing(null); setCreating(false); }} className="px-4 py-2 bg-white/10 text-white rounded-lg">
              Cancelar
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg">
              Guardar
            </button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <h3 className="text-white font-semibold mb-4">Datos Generales</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Nombre Comercial (ES) *</label>
              <Input
                value={form.business_name?.es || ''}
                onChange={(e) => setForm({ ...form, business_name: { ...form.business_name, es: e.target.value } })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Nombre del Proveedor</label>
              <Input
                value={form.provider_name || ''}
                onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Ciudad *</label>
              <Input
                value={form.city || ''}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">País</label>
              <Input
                value={form.country || ''}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Teléfono</label>
              <Input
                value={form.phone || ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">WhatsApp</label>
              <Input
                value={form.whatsapp || ''}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-white/70 text-sm mb-2">Descripción</label>
              <Textarea
                value={form.description?.es || ''}
                onChange={(e) => setForm({ ...form, description: { ...form.description, es: e.target.value } })}
                rows={3}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Imagen de Portada (URL)</label>
              <Input
                value={form.cover_image || ''}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Estado</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 bg-[#0a0a0a] text-white border border-white/10 rounded-lg"
              >
                <option value="draft">Borrador</option>
                <option value="pending">Pendiente</option>
                <option value="approved">Aprobado</option>
                <option value="rejected">Rechazado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Vehicle Types */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <h3 className="text-white font-semibold mb-4">Tipos de Vehículo</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {vehicleTypeOptions.map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(form.vehicle_types || []).includes(type)}
                  onChange={() => toggleArrayItem('vehicle_types', type)}
                  className="w-4 h-4 rounded bg-white/10 border-white/20"
                />
                <span className="text-white/70 text-sm">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Vehicle Details */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <h3 className="text-white font-semibold mb-4">Detalles del Vehículo</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Marca</label>
              <Input value={form.brand || ''} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Modelo</label>
              <Input value={form.model_name || ''} onChange={(e) => setForm({ ...form, model_name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Año</label>
              <Input value={form.year || ''} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Capacidad Pasajeros</label>
              <Input type="number" value={form.passenger_capacity || 0} onChange={(e) => setForm({ ...form, passenger_capacity: parseInt(e.target.value) || 0 })} className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>
        </div>

        {/* Service Levels */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <h3 className="text-white font-semibold mb-4">Niveles de Servicio</h3>
          <div className="flex flex-wrap gap-3">
            {serviceLevelOptions.map((level) => (
              <label key={level} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(form.service_levels || []).includes(level.toLowerCase())}
                  onChange={() => toggleArrayItem('service_levels', level.toLowerCase())}
                  className="w-4 h-4 rounded bg-white/10 border-white/20"
                />
                <span className="text-white/70 text-sm">{level}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Service Modes */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <h3 className="text-white font-semibold mb-4">Modalidades</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { field: 'with_driver', label: 'Con chofer' },
              { field: 'without_driver', label: 'Sin chofer' },
              { field: 'by_hour', label: 'Por hora' },
              { field: 'by_day', label: 'Por día' },
              { field: 'by_transfer', label: 'Por traslado' },
              { field: 'by_event', label: 'Por evento' },
              { field: 'airport_service', label: 'Aeropuerto' },
              { field: 'night_availability', label: 'Nocturno' },
              { field: 'out_of_town', label: 'Foráneo' },
              { field: 'tours_long_trips', label: 'Giras' },
            ].map(({ field, label }) => (
              <label key={field} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form[field] || false}
                  onChange={(e) => setForm({ ...form, [field]: e.target.checked })}
                  className="w-4 h-4 rounded bg-white/10 border-white/20"
                />
                <span className="text-white/70 text-sm">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Amenities */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <h3 className="text-white font-semibold mb-4">Amenidades</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {amenityOptions.map((amenity) => (
              <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(form.amenities || []).includes(amenity.toLowerCase())}
                  onChange={() => toggleArrayItem('amenities', amenity.toLowerCase())}
                  className="w-4 h-4 rounded bg-white/10 border-white/20"
                />
                <span className="text-white/70 text-sm">{amenity}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Featured */}
        <div className="glass-dark rounded-xl p-6 border border-white/5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.featured || false}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              className="w-4 h-4 rounded bg-white/10 border-white/20"
            />
            <span className="text-white flex items-center gap-2"><Star size={16} className="text-[#D4AF37]" /> Destacar en homepage</span>
          </label>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6" data-testid="transport-list">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {['all', 'approved', 'pending', 'draft'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                filter === f ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white/70 hover:bg-white/15'
              }`}
            >
              {f === 'all' ? 'Todos' : f === 'approved' ? 'Aprobados' : f === 'pending' ? 'Pendientes' : 'Borradores'}
            </button>
          ))}
        </div>
        <button onClick={startCreate} className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg">
          <Plus size={18} /> Nuevo Transporte
        </button>
      </div>

      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <Car size={48} className="mx-auto text-white/20 mb-4" />
          <p className="text-white/50">No hay servicios de transporte</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => (
            <div key={item.transport_id} className="glass-dark rounded-xl overflow-hidden border border-white/5">
              <div className="aspect-video relative">
                <img
                  src={item.cover_image || 'https://images.pexels.com/photos/1035108/pexels-photo-1035108.jpeg?auto=compress&cs=tinysrgb&w=800'}
                  alt={item.business_name?.es}
                  className="w-full h-full object-cover"
                />
                {item.featured && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-[#D4AF37] text-black text-xs rounded-full flex items-center gap-1">
                    <Star size={12} /> Destacado
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-semibold truncate">{item.business_name?.es || 'Sin nombre'}</h3>
                  <StatusBadge status={item.status} />
                </div>
                <div className="flex items-center gap-4 text-white/50 text-sm mb-3">
                  <span className="flex items-center gap-1"><MapPin size={14} /> {item.city || 'Sin ciudad'}</span>
                  <span className="flex items-center gap-1"><Users size={14} /> {item.passenger_capacity || 0} pax</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {(item.vehicle_types || []).slice(0, 2).map((type) => (
                    <span key={type} className="px-2 py-0.5 bg-white/10 text-white/70 text-xs rounded">{type}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(item)} className="p-2 bg-white/10 text-white rounded-lg hover:bg-white/15">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleToggleFeatured(item.transport_id)} className={`p-2 rounded-lg ${item.featured ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white/50'}`}>
                      <Star size={16} />
                    </button>
                    <button onClick={() => handleDelete(item.transport_id)} className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {item.status === 'pending' && (
                    <div className="flex gap-2">
                      <button onClick={() => handleApprove(item.transport_id, 'approved')} className="p-2 bg-green-500/20 text-green-400 rounded-lg">
                        <CheckCircle size={16} />
                      </button>
                      <button onClick={() => handleApprove(item.transport_id, 'rejected')} className="p-2 bg-red-500/20 text-red-400 rounded-lg">
                        <XCircle size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
